<script lang="ts">
    import { onDestroy } from "svelte";
    import { t } from "$lib/i18n/translations";
    import { downloadButtonState } from "$lib/state/omnibox";
    import type { CobaltDownloadButtonState } from "$lib/types/omnibox";

    // Importamos los iconos reales que querías
    import IconDownload from "@tabler/icons-svelte/IconDownload.svelte";
    import IconLoader2 from "@tabler/icons-svelte/IconLoader2.svelte";
    import IconCheck from "@tabler/icons-svelte/IconCheck.svelte";
    import IconX from "@tabler/icons-svelte/IconX.svelte";

    export let url: string;
    export let disabled = false;
    export let loading = false;

    let currentState: DownloadButtonState = "idle";
    $: buttonAltText = $t("a11y.save.download");

    type DownloadButtonState = "idle" | "think" | "check" | "done" | "error";

    const unsubscribe = downloadButtonState.subscribe(
        (state: CobaltDownloadButtonState) => {
            currentState = state;
            disabled = state !== "idle";
            loading = state === "think" || state === "check";

            buttonAltText = $t({
                idle: "a11y.save.download",
                think: "a11y.save.download.think",
                check: "a11y.save.download.check",
                done: "a11y.save.download.done",
                error: "a11y.save.download.error",
            }[state]);

            const final: DownloadButtonState[] = ["done", "error"];
            if (final.includes(state)) {
                setTimeout(() => downloadButtonState.set("idle"), 1500);
            }
        }
    );

    onDestroy(() => unsubscribe());
</script>

<!-- Le quitamos el on:click antiguo porque ahora lo controla el Omnibox padre -->
<button
    id="download-button"
    {disabled}
    aria-label={buttonAltText}
    class:is-loading={loading}
    class:is-error={currentState === "error"}
    class:is-done={currentState === "done"}
>
    <div id="download-state">
        {#if currentState === "think" || currentState === "check"}
            <IconLoader2 class="spin-icon" />
        {:else if currentState === "done"}
            <IconCheck />
        {:else if currentState === "error"}
            <IconX />
        {:else}
            <IconDownload />
        {/if}
    </div>
</button>

<style>
    #download-button {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-width: 48px;
        width: 48px;
        border-radius: 0;
        
        /* Botón Rojo 1312 por defecto */
        background: var(--primary);
        color: white;
        border: none;
        
        border-top-right-radius: var(--border-radius);
        border-bottom-right-radius: var(--border-radius);
        cursor: pointer;
        transition: all 0.2s ease;
    }

    /* Ajuste para idiomas de derecha a izquierda */
    #download-button:dir(rtl) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-top-left-radius: var(--border-radius);
        border-bottom-left-radius: var(--border-radius);
    }

    #download-state {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    #download-state :global(svg) {
        width: 22px;
        height: 22px;
        stroke-width: 2px;
    }

    /* ESTADOS DEL BOTÓN */
    #download-button:disabled {
        background: #27272a; /* Gris oscuro si está desactivado */
        color: var(--gray);
        cursor: not-allowed;
    }

    #download-button.is-done {
        background: #34a853 !important; /* Verde Google si tiene éxito */
        color: white;
    }

    #download-button.is-error {
        background: #cf222e !important; /* Rojo Error si falla */
        color: white;
    }

    /* Animación de la ruleta de carga */
    :global(.spin-icon) {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        100% { transform: rotate(360deg); }
    }

    @media (hover: hover) {
        #download-button:hover:not(:disabled) {
            filter: brightness(1.1); /* Brilla un poco al pasar el ratón */
        }
    }

    #download-button:active:not(:disabled) {
        filter: brightness(0.9);
        transform: scale(0.95);
    }
</style>